import { useParams, useSearchParams, useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CheckInHeader from '../../components/check-in/CheckInHeader';
import CheckInNavBar from '../../components/check-in/CheckInNavBar';
import TipBox from '../../components/check-in/TipBox';
import StrategyDetailSkeleton from '../../components/check-in/StrategyDetailSkeleton';
import { useCheckInStrategyDetail, useCompleteStrategy } from '../../hooks/useCheckIn';
import { getSubEmotion } from '../../data/check-in';
import placeholderBackground from '../../../assets/icons/check-in/placeholder-background.svg';
import { containerVariants, itemVariants } from '../../lib/animations';
import { useAnalytics } from '../../contexts/AnalyticsContext';

export default function CheckInStrategyDetailPage() {
  const { track } = useAnalytics();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: strategy, isLoading } = useCheckInStrategyDetail(id ?? '');
  const completeMutation = useCompleteStrategy();

  const category = searchParams.get('category') ?? '';
  const subEmotionParam = searchParams.get('sub') ?? '';
  const subEmotion = category && subEmotionParam ? getSubEmotion(category, subEmotionParam) : undefined;

  if (!id) {
    return <Navigate to="/check-in/weather" replace />;
  }

  const handleComplete = () => {
    track('strategy_complete', { strategy_id: String(id), category, sub_emotion: subEmotionParam });
    completeMutation.mutate(id, {
      onSuccess: () => {
        navigate('/check-in/completed');
      },
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <CheckInHeader />
      <CheckInNavBar label="BACK TO TINY STRATEGIES" />

      {isLoading ? (
        <div className="mt-4">
          <StrategyDetailSkeleton />
        </div>
      ) : strategy ? (
        <motion.div
          className="flex flex-col items-center px-6 mt-4 gap-6 pb-8 w-full max-w-[430px] mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Title section */}
          <motion.div className="text-center max-w-[382px]" variants={itemVariants}>
            <h2
              className="text-[#1B2831] font-bold text-[32px] md:text-[34px] lg:text-[36px]"
              style={{ fontFamily: 'DM Sans', letterSpacing: '-1.28px' }}
            >
              {strategy.title}
            </h2>
            <p
              className="text-base text-[#4D5762] mt-1"
              style={{ fontFamily: 'DM Sans' }}
            >
              {strategy.subtitle}
            </p>
          </motion.div>

          {/* Illustration */}
          <motion.div variants={itemVariants}>
            {strategy.imageUrl ? (
              <div className="w-full max-w-[382px] rounded-lg overflow-hidden">
                <img
                  src={strategy.imageUrl}
                  alt={strategy.title}
                  className="w-full h-auto object-cover rounded-lg"
                  style={{ aspectRatio: '382/272' }}
                />
              </div>
            ) : (
              <div className="w-full max-w-[382px] rounded-lg overflow-hidden relative" style={{ aspectRatio: '382/272' }}>
                <img
                  src={placeholderBackground}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {subEmotion && (
                  <img
                    src={subEmotion.asset}
                    alt={subEmotion.name}
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[45%]"
                  />
                )}
              </div>
            )}
          </motion.div>

          {/* Read Aloud section */}
          <motion.div className="w-full max-w-[382px]" variants={itemVariants}>
            <span
              className="text-xs font-black uppercase text-[#1B2831] block mb-2"
              style={{ fontFamily: 'DM Sans', letterSpacing: '0.72px' }}
            >
              READ ALOUD
            </span>
            <p
              className="text-base text-[#1B2831] whitespace-pre-wrap"
              style={{ fontFamily: 'DM Sans' }}
            >
              {strategy.description}
            </p>
          </motion.div>

          {/* Tip section */}
          {strategy.tip && (
            <motion.div className="w-full max-w-[382px]" variants={itemVariants}>
              <TipBox>{strategy.tip}</TipBox>
            </motion.div>
          )}

          {/* Marked as completed button */}
          <motion.button
            onClick={handleComplete}
            disabled={completeMutation.isPending}
            className="w-full max-w-[382px] h-12 rounded-xl border-2 border-[#01A7C8] bg-white flex items-center justify-center gap-2.5 hover:bg-[#C7EDEF]/30 transition-colors disabled:opacity-50"
            variants={itemVariants}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17l-5-5" stroke="#007CAD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span
              className="text-xs font-black uppercase text-[#007CAD]"
              style={{ fontFamily: 'DM Sans', letterSpacing: '0.72px' }}
            >
              {completeMutation.isPending ? 'SAVING...' : 'MARKED AS COMPLETED'}
            </span>
          </motion.button>
        </motion.div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-base text-[#1B2831]" style={{ fontFamily: 'DM Sans' }}>
            Strategy not found.
          </p>
        </div>
      )}
    </div>
  );
}
